import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useLang } from "@/lib/i18n";
import { readConsent, saveConsent, acceptAll, refuseAll } from "@/lib/cookieConsent";

/**
 * Bottom banner that appears as long as the user has not made a choice.
 * Also exposes a "manage cookies" experience that can be opened from anywhere
 * via the global event `sukana:open-cookie-settings` (dispatched by Footer link).
 */
export default function CookieBanner() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setVisible(true);
    else { setAnalytics(existing.analytics); setMarketing(existing.marketing); }

    // Allow anyone to re-open the settings from anywhere (footer link, etc.)
    const openHandler = () => {
      const current = readConsent();
      setAnalytics(!!current?.analytics);
      setMarketing(!!current?.marketing);
      setConfigOpen(true);
    };
    window.addEventListener("sukana:open-cookie-settings", openHandler);
    return () => window.removeEventListener("sukana:open-cookie-settings", openHandler);
  }, []);

  const onAcceptAll = () => { acceptAll(); setVisible(false); setConfigOpen(false); };
  const onRefuseAll = () => { refuseAll(); setVisible(false); setConfigOpen(false); };
  const onSaveConfig = () => { saveConsent({ analytics, marketing }); setVisible(false); setConfigOpen(false); };

  return (
    <>
      {visible && (
        <div
          role="dialog"
          aria-label="Consentement cookies"
          className="fixed bottom-4 left-4 right-4 sm:left-6 sm:right-auto sm:max-w-md z-[9998] bg-white border border-[#1A3322]/15 rounded-2xl shadow-2xl p-5 backdrop-blur"
          data-testid="cookie-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FFF7ED] flex items-center justify-center shrink-0">
              <Cookie className="w-5 h-5 text-[#C85A32]"/>
            </div>
            <div className="flex-1 min-w-0">
              <p className="heading font-bold text-[15px] text-[#111812]" data-testid="cookie-banner-title">
                {t("cookies.banner_title")}
              </p>
              <p className="text-[12.5px] text-[#4B5563] mt-1 leading-snug">
                {t("cookies.banner_desc")}{" "}
                <Link to="/cgv" className="underline text-[#1A3322] hover:text-[#C85A32]">{t("cookies.learn_more")}</Link>
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={onAcceptAll} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full text-xs h-8 px-4" data-testid="cookie-accept-all">
                  <Check className="w-3.5 h-3.5 mr-1.5"/>{t("cookies.accept_all")}
                </Button>
                <Button size="sm" variant="outline" onClick={onRefuseAll} className="rounded-full text-xs h-8 px-4 border-[#1A3322]/30" data-testid="cookie-refuse-all">
                  <X className="w-3.5 h-3.5 mr-1.5"/>{t("cookies.refuse_all")}
                </Button>
                <button
                  onClick={() => setConfigOpen(true)}
                  className="text-xs text-[#1A3322] hover:text-[#C85A32] underline underline-offset-2 font-semibold h-8 px-2"
                  data-testid="cookie-configure"
                >
                  <Settings2 className="w-3.5 h-3.5 inline mr-1"/>{t("cookies.configure")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Configuration modal (per-category toggles) */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-md" data-testid="cookie-config-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-[#C85A32]"/>{t("cookies.config_title")}
            </DialogTitle>
            <DialogDescription>{t("cookies.config_desc")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            {/* Necessary — always on */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg bg-[#F5F5F0]" data-testid="cookie-cat-necessary">
              <div className="flex-1 min-w-0">
                <p className="heading font-semibold text-sm text-[#111812]">{t("cookies.cat_necessary_title")}</p>
                <p className="text-[12px] text-[#4B5563] mt-0.5">{t("cookies.cat_necessary_desc")}</p>
              </div>
              <Switch checked={true} disabled aria-label="Cookies nécessaires (activés)" data-testid="cookie-toggle-necessary"/>
            </div>

            {/* Analytics */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-[#1A3322]/10" data-testid="cookie-cat-analytics">
              <div className="flex-1 min-w-0">
                <p className="heading font-semibold text-sm text-[#111812]">{t("cookies.cat_analytics_title")}</p>
                <p className="text-[12px] text-[#4B5563] mt-0.5">{t("cookies.cat_analytics_desc")}</p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="Cookies analytiques" data-testid="cookie-toggle-analytics"/>
            </div>

            {/* Marketing */}
            <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-[#1A3322]/10" data-testid="cookie-cat-marketing">
              <div className="flex-1 min-w-0">
                <p className="heading font-semibold text-sm text-[#111812]">{t("cookies.cat_marketing_title")}</p>
                <p className="text-[12px] text-[#4B5563] mt-0.5">{t("cookies.cat_marketing_desc")}</p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} aria-label="Cookies marketing" data-testid="cookie-toggle-marketing"/>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 flex-wrap">
            <Button variant="outline" onClick={onRefuseAll} className="rounded-full text-xs" data-testid="cookie-config-refuse">
              {t("cookies.refuse_all")}
            </Button>
            <Button onClick={onSaveConfig} variant="outline" className="rounded-full text-xs" data-testid="cookie-config-save">
              {t("cookies.save_choices")}
            </Button>
            <Button onClick={onAcceptAll} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full text-xs" data-testid="cookie-config-accept-all">
              {t("cookies.accept_all")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
